import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, ArrowUpDown, ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import { format, subDays, isAfter, isBefore } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useUsageLimits } from '@/hooks/useUsageLimits';
import { SoftUpgradePrompt } from '@/components/shared/SoftUpgradePrompt';


type SortKey = 'date' | 'symbol' | 'side' | 'price' | 'quantity' | 'pnl' | 'strategy';
type SortDir = 'asc' | 'desc';

interface TradeRow {
  id: string;
  date: string;
  symbol: string;
  side: string;
  price: number | null;
  quantity: number;
  pnl: number;
  strategy: string;
  strategyInstanceId: string;
}

const TradeJournal = () => {
  const { user } = useAuth();
  const { canExportCSV, checkLimit } = useUsageLimits();
  const csvCheck = checkLimit('csv_exports');
  const [csvLimitHit, setCsvLimitHit] = useState(false);
  const [dateRange, setDateRange] = useState('30');
  const [symbolFilter, setSymbolFilter] = useState('all');
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [sideFilter, setSideFilter] = useState('all');
  const [pnlFilter, setPnlFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);
  const perPage = 20;

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['trade-journal-orders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('strategy_orders')
        .select('id, created_at, symbol, side, price, quantity, strategy_instance_id, updated_at')
        .eq('status', 'filled')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: instances = [] } = useQuery({
    queryKey: ['trade-journal-instances'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('strategy_instances')
        .select('id, strategy_name');
      if (error) throw error;
      return data || [];
    },
  });

  const instanceMap = useMemo(() => {
    const m: Record<string, string> = {};
    instances.forEach((i) => { m[i.id] = i.strategy_name; });
    return m;
  }, [instances]);

  const trades: TradeRow[] = useMemo(() =>
    orders.map((o) => ({
      id: o.id,
      date: o.created_at,
      symbol: o.symbol,
      side: o.side,
      price: o.price,
      quantity: Number(o.quantity),
      pnl: o.price ? Number(o.price) * Number(o.quantity) * (o.side === 'sell' ? 1 : -1) : 0,
      strategy: instanceMap[o.strategy_instance_id] || 'Unknown',
      strategyInstanceId: o.strategy_instance_id,
    })), [orders, instanceMap]);

  const filteredTrades = useMemo(() => {
    let result = [...trades];
    // date
    if (dateRange !== 'all') {
      const cutoff = subDays(new Date(), parseInt(dateRange));
      result = result.filter((t) => isAfter(new Date(t.date), cutoff));
    }
    if (symbolFilter !== 'all') result = result.filter((t) => t.symbol === symbolFilter);
    if (strategyFilter !== 'all') result = result.filter((t) => t.strategy === strategyFilter);
    if (sideFilter !== 'all') result = result.filter((t) => t.side === sideFilter);
    if (pnlFilter === 'profitable') result = result.filter((t) => t.pnl > 0);
    if (pnlFilter === 'losing') result = result.filter((t) => t.pnl < 0);

    result.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'date': cmp = new Date(a.date).getTime() - new Date(b.date).getTime(); break;
        case 'symbol': cmp = a.symbol.localeCompare(b.symbol); break;
        case 'side': cmp = a.side.localeCompare(b.side); break;
        case 'price': cmp = (a.price || 0) - (b.price || 0); break;
        case 'quantity': cmp = a.quantity - b.quantity; break;
        case 'pnl': cmp = a.pnl - b.pnl; break;
        case 'strategy': cmp = a.strategy.localeCompare(b.strategy); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [trades, dateRange, symbolFilter, strategyFilter, sideFilter, pnlFilter, sortKey, sortDir]);

  const symbols = useMemo(() => [...new Set(trades.map((t) => t.symbol))].sort(), [trades]);
  const strategies = useMemo(() => [...new Set(trades.map((t) => t.strategy))].sort(), [trades]);

  const stats = useMemo(() => {
    const total = filteredTrades.length;
    const totalPnl = filteredTrades.reduce((s, t) => s + t.pnl, 0);
    const wins = filteredTrades.filter((t) => t.pnl > 0).length;
    const winRate = total > 0 ? (wins / total) * 100 : 0;
    const best = total > 0 ? Math.max(...filteredTrades.map((t) => t.pnl)) : 0;
    const worst = total > 0 ? Math.min(...filteredTrades.map((t) => t.pnl)) : 0;
    return { total, totalPnl, winRate, best, worst };
  }, [filteredTrades]);

  const pagedTrades = filteredTrades.slice(page * perPage, (page + 1) * perPage);
  const totalPages = Math.max(1, Math.ceil(filteredTrades.length / perPage));

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const exportCSV = async () => {
    if (!user || !canExportCSV) {
      setCsvLimitHit(true);
      return;
    }
    try {
      await supabase.rpc('increment_usage', { p_user_id: user.id, p_metric: 'csv_exports' });
    } catch (e) {
      console.error('Failed to track usage:', e);
    }
    const header = 'Date,Symbol,Side,Price,Quantity,PnL,Strategy';
    const rows = filteredTrades.map((t) =>
      `${format(new Date(t.date), 'yyyy-MM-dd HH:mm')},${t.symbol},${t.side},${t.price ?? ''},${t.quantity},${t.pnl.toFixed(2)},${t.strategy}`
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trade-journal-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Trade Journal</h1>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={exportCSV} variant="outline" size="sm" disabled={csvLimitHit || !canExportCSV}>
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          {(csvLimitHit || !canExportCSV) && (
            <SoftUpgradePrompt
              feature="CSV exports"
              message={csvCheck.message || 'CSV export limit reached'}
              currentUsage={csvCheck.used}
              limit={csvCheck.limit}
              className="max-w-xs"
            />
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Select value={dateRange} onValueChange={(v) => { setDateRange(v); setPage(0); }}>
              <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            <Select value={symbolFilter} onValueChange={(v) => { setSymbolFilter(v); setPage(0); }}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Symbol" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Symbols</SelectItem>
                {symbols.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={strategyFilter} onValueChange={(v) => { setStrategyFilter(v); setPage(0); }}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Strategy" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Strategies</SelectItem>
                {strategies.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sideFilter} onValueChange={(v) => { setSideFilter(v); setPage(0); }}>
              <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sides</SelectItem>
                <SelectItem value="buy">Buy Only</SelectItem>
                <SelectItem value="sell">Sell Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={pnlFilter} onValueChange={(v) => { setPnlFilter(v); setPage(0); }}>
              <SelectTrigger className="h-11 col-span-2 lg:col-span-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All PnL</SelectItem>
                <SelectItem value="profitable">Profitable</SelectItem>
                <SelectItem value="losing">Losing</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(dateRange !== '30' || symbolFilter !== 'all' || strategyFilter !== 'all' || sideFilter !== 'all' || pnlFilter !== 'all') && (
            <button
              className="text-xs text-primary hover:underline mt-2"
              onClick={() => { setDateRange('30'); setSymbolFilter('all'); setStrategyFilter('all'); setSideFilter('all'); setPnlFilter('all'); setPage(0); }}
            >
              Clear filters
            </button>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><CardContent className="pt-4 pb-4 text-center">
          <p className="text-xs text-muted-foreground">Total Trades</p>
          <p className="text-xl font-bold text-foreground">{stats.total}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4 text-center">
          <p className="text-xs text-muted-foreground">Total PnL</p>
          <p className={`text-xl font-bold ${stats.totalPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            ${stats.totalPnl.toFixed(2)}
          </p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4 text-center">
          <p className="text-xs text-muted-foreground">Win Rate</p>
          <p className="text-xl font-bold text-foreground">{stats.winRate.toFixed(1)}%</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4 text-center">
          <p className="text-xs text-muted-foreground">Best Trade</p>
          <p className="text-xl font-bold text-green-500">${stats.best.toFixed(2)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-4 text-center">
          <p className="text-xs text-muted-foreground">Worst Trade</p>
          <p className="text-xl font-bold text-red-500">${stats.worst.toFixed(2)}</p>
        </CardContent></Card>
      </div>

      {/* Trades Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading trades…</div>
          ) : filteredTrades.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No trades found matching your filters.</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    {([
                      ['date', 'Date'],
                      ['symbol', 'Symbol'],
                      ['side', 'Side'],
                      ['price', 'Price'],
                      ['quantity', 'Qty'],
                      ['pnl', 'PnL'],
                      ['strategy', 'Strategy'],
                    ] as [SortKey, string][]).map(([key, label]) => (
                      <TableHead key={key} className="cursor-pointer select-none" onClick={() => handleSort(key)}>
                        <span className="flex items-center gap-1">
                          {label}
                          {sortKey === key && <ArrowUpDown className="h-3 w-3" />}
                        </span>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedTrades.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="text-xs">{format(new Date(t.date), 'MMM dd, yyyy HH:mm')}</TableCell>
                      <TableCell className="font-medium">{t.symbol}</TableCell>
                      <TableCell>
                        <Badge variant={t.side === 'buy' ? 'default' : 'destructive'} className="text-xs">
                          {t.side.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>${t.price?.toFixed(2) ?? '—'}</TableCell>
                      <TableCell>{t.quantity}</TableCell>
                      <TableCell className={t.pnl >= 0 ? 'text-green-500 font-medium' : 'text-red-500 font-medium'}>
                        ${t.pnl.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{t.strategy}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between p-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Showing {page * perPage + 1}–{Math.min((page + 1) * perPage, filteredTrades.length)} of {filteredTrades.length}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TradeJournal;
